import React from "react";
import { motion } from "framer-motion";
import { Box, Tooltip } from "@mui/material";
import {
  BuildCircle,
  RocketLaunch,
  CheckCircle,
  Error,
  PauseCircle,
} from "@mui/icons-material";

const statusConfig = {
  building: {
    color: "#f59e0b",
    bgColor: "rgba(245, 158, 11, 0.15)",
    animate: true,
    icon: BuildCircle,
    label: "Building",
  },
  deploying: {
    color: "#F97316",
    bgColor: "rgba(234, 88, 12, 0.15)",
    animate: true,
    icon: RocketLaunch,
    label: "Deploying",
  },
  live: {
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.15)",
    animate: false,
    icon: CheckCircle,
    label: "Live",
  },
  error: {
    color: "#ef4444",
    bgColor: "rgba(239, 68, 68, 0.15)",
    animate: false,
    icon: Error,
    label: "Error",
  },
  idle: {
    color: "#64748b",
    bgColor: "rgba(100, 116, 139, 0.15)",
    animate: false,
    icon: PauseCircle,
    label: "Sleeping",
  },
};

const pulseAnimation = {
  scale: [1, 1.2, 1],
  boxShadow: [
    "0 0 0 0 currentColor",
    "0 0 0 8px transparent",
    "0 0 0 0 currentColor",
  ],
};

const glowAnimation = {
  opacity: [0.4, 1, 0.4],
};

export function StatusOrb({ status, size = "medium", showTooltip = true }) {
  const config = statusConfig[status] || statusConfig.idle;
  const Icon = config.icon;

  const sizeMap = {
    small: { orb: 24, icon: 14 },
    medium: { orb: 32, icon: 18 },
    large: { orb: 40, icon: 22 },
  };

  const dimensions = sizeMap[size] || sizeMap.medium;

  const orbContent = (
    <Box
      component={motion.div}
      animate={config.animate ? pulseAnimation : {}}
      transition={config.animate ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : {}}
      sx={{
        width: dimensions.orb,
        height: dimensions.orb,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: config.bgColor,
        color: config.color,
        position: "relative",
      }}
    >
      {/* Glow ring for active states */}
      {config.animate && (
        <Box
          component={motion.div}
          animate={glowAnimation}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: "absolute",
            inset: -2,
            borderRadius: "50%",
            border: `2px solid ${config.color}`,
            pointerEvents: "none",
          }}
        />
      )}
      <Icon sx={{ fontSize: dimensions.icon }} />
    </Box>
  );

  if (showTooltip) {
    return (
      <Tooltip title={config.label} arrow placement="top">
        {orbContent}
      </Tooltip>
    );
  }

  return orbContent;
}

export { statusConfig };
export default StatusOrb;
